import { ActivatedRoute } from '@angular/router'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Observable, Subject, catchError, combineLatest, map, mergeMap, of, takeUntil } from 'rxjs'
import { ProductLibrary } from '../../../domain/ProductLibrary'
import { ProductLibraryService } from '../../../services/product-library.service'
import { Title } from '@angular/platform-browser'
import { Venue } from '../../../domain/Venue'
import { VenueService } from '../../../services/venue.service'
import { Voucher } from '../../../domain/Voucher'
import { environment } from '../../../../environments/environment'
import { truncateRichTextHtml } from '../../../utilities/rich-text'
import { LandingPageModalService } from '../../modal/landing-page-modal.service'

type VoucherStorefrontViewModel = {
    canPurchaseVouchers: boolean
    landingPagePath: string
    venue: Venue
    vouchers: VoucherStorefrontVoucherViewModel[]
}

type VoucherStorefrontVoucherViewModel = {
    voucher: Voucher
    descriptionContent: string | null
    formattedLowestPrice: string
    expiryDate: Date | null
}

@Component({
    selector: 'app-landing-page-voucher-storefront',
    templateUrl: './voucher-storefront.component.html',
})
export class VoucherStorefrontComponent implements OnInit, OnDestroy {

    marketingUrl = environment.marketingUrl
    descriptionCharacterLimit = 280
    storefront$!: Observable<VoucherStorefrontViewModel>
    protected readonly defaultVoucherImageAspectRatio = 16 / 9
    protected voucherImageAspectRatios: Record<string, number> = {}
    private onDestroy$ = new Subject<void>()

    constructor(
        private route: ActivatedRoute,
        private titleService: Title,
        private productLibraryService: ProductLibraryService,
        private venueService: VenueService,
        private landingPageModalService: LandingPageModalService,
    ) { }

    ngOnInit() {
        this.storefront$ = this.route.paramMap
            .pipe(
                mergeMap(params => {
                    const venueSlug = params.get('venueSlug') ?? ''
                    return this.venueService.getWithSlug(venueSlug)
                        .pipe(
                            mergeMap(venue => combineLatest({
                                venue: of(venue),
                                productLibrary: this.productLibraryService.getForVenueId(venue.id)
                                    .pipe(
                                        catchError(() => of(this.makeEmptyProductLibrary(venue.id)))
                                    ),
                            })),
                            map(({ venue, productLibrary }) => ({
                                canPurchaseVouchers: this.canPurchaseVouchers(venue),
                                landingPagePath: `/discover/${venueSlug}`,
                                venue,
                                vouchers: this.filterOnSaleVouchers(productLibrary.vouchers)
                                    .map(voucher => this.makeVoucherViewModel(voucher, venue)),
                            }))
                        )
                })
            )
        this.storefront$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(storefront => {
                this.titleService.setTitle(`${storefront.venue.voucherDisplayName()} Gift Vouchers`)
            })
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    openVoucherPurchase(storefront: VoucherStorefrontViewModel, voucherId: string) {
        if (!storefront.canPurchaseVouchers) {
            return
        }
        this.landingPageModalService.openVoucherModal({
            organisationId: storefront.venue.organisationId,
            businessId: storefront.venue.businessId,
            venueId: storefront.venue.id,
            voucherId,
        })
    }

    protected voucherImageAspectRatio(voucherId: string): number {
        return this.voucherImageAspectRatios[voucherId] ?? this.defaultVoucherImageAspectRatio
    }

    protected onVoucherImageLoaded(voucherId: string, event: Event) {
        const image = event.target as HTMLImageElement
        if (!image.naturalWidth || !image.naturalHeight) {
            return
        }
        this.voucherImageAspectRatios[voucherId] = image.naturalWidth / image.naturalHeight
    }

    private canPurchaseVouchers(venue: Venue): boolean {
        return Boolean(venue.paymentsAccountForCapability('VoucherPayments'))
    }

    private makeVoucherViewModel(
        voucher: Voucher,
        venue: Venue
    ): VoucherStorefrontVoucherViewModel {
        return {
            voucher,
            descriptionContent: voucher.description
                ? truncateRichTextHtml(voucher.description, this.descriptionCharacterLimit)
                : null,
            formattedLowestPrice: this.formatMoney(voucher.lowestPricedAmount, venue.currencyCode),
            expiryDate: voucher.getExpiryDateFromDate(new Date()),
        }
    }

    private formatMoney(amount: number | null, currencyCode: string | null): string {
        if (amount === null) {
            return 'Unavailable'
        }
        const value = amount / 100
        if (!currencyCode) {
            return value.toFixed(2)
        }
        try {
            return new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: currencyCode,
            }).format(value)
        } catch {
            return value.toFixed(2)
        }
    }

    private filterOnSaleVouchers(vouchers: Voucher[]): Voucher[] {
        return vouchers
            .filter(voucher => voucher.isOnSale)
            .sort((firstVoucher, secondVoucher) => {
                const firstAmount = firstVoucher.lowestPricedAmount ?? Number.MAX_SAFE_INTEGER
                const secondAmount = secondVoucher.lowestPricedAmount ?? Number.MAX_SAFE_INTEGER
                return firstAmount - secondAmount
            })
    }

    private makeEmptyProductLibrary(venueId: string): ProductLibrary {
        return new ProductLibrary(`missing-product-library-${venueId}`, [], [], [], [])
    }

    protected readonly environment = environment
}
